/*
 * ez-carousel — the ONE finite-carousel transport engine (design_system.md §6 Carousel cluster).
 *
 * Native CSS scroll-snap is the transport; this module wires the CONTROLS on top of it for EVERY
 * finite ez carousel — the product rail, the hero pager, the image/marketing banner and the
 * shoppable gallery — replacing four near-identical per-section modules. One vanilla custom
 * element, defined once, no framework and no external slider library (theme-foundation.md §A5/§A7).
 * AUTOPLAY IS OFF everywhere by design (user-swiped only). Reduced-motion is respected on the
 * smooth-scroll calls; the snap itself is neutralised in CSS.
 *
 * The INFINITE (wrap-around) banner is DELIBERATELY NOT driven by this engine — it keeps its own
 * ez-infinite-banner.js wrap mechanism (bug-121: never a `scroll-behavior:smooth` on a track a JS
 * reposition drives) and only borrows the shared nav chrome + shell. Do NOT absorb its wrap logic.
 *
 * Behaviour is selected per instance by a NAMED BUNDLE on the element:
 *   data-ez-bundle   product-rail | collection-rail | banner-carousel | hero-pager | gallery-scene
 *   data-ez-cell     CSS selector for a transport cell (e.g. ".ez-card", ".ez-hero__slide")
 *   data-ez-free-scroll   present -> free-scroll variant (collection-rail: no snap-to-cell, no dots)
 *
 * SNAP-NEUTRAL BASE INVARIANT: this engine never writes `scroll-snap-type` or `scroll-behavior`
 * onto a track — each bundle's snap + smooth is CSS-owned (ez-carousel-styles), which is exactly
 * what keeps the infinite quarantine's no-smooth-on-the-loop-track contract intact even though the
 * shared transport base is common.
 */
(function () {
  if (customElements.get('ez-carousel')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Per-bundle transport config. step: how an arrow/dot advances. index: how the active dot is
  // resolved from scroll position. progress: whether a scroll-progress fill is synced (scaleX).
  var BUNDLES = {
    'product-rail':    { step: 'proximity',  index: 'nearest',     progress: true },
    'collection-rail': { step: 'viewport',   index: 'nearest',     progress: false, free: true },
    'banner-carousel': { step: 'mandatory',  index: 'nearest',     progress: true },
    'hero-pager':      { step: 'viewport',   index: 'clientWidth', progress: false },
    'gallery-scene':   { step: 'viewport',   index: 'perSlide',    progress: true }
  };

  class EzCarousel extends HTMLElement {
    connectedCallback() {
      // Subclasses (rail recommendations) may defer boot until after an async swap.
      this._boot();
    }

    // Wire the native-scroll-snap transport controls. Safe to call again after a subclass swaps
    // the track markup in (recommendations). No-op if there is no track yet.
    _boot() {
      this.cfg = BUNDLES[this.dataset.ezBundle] || BUNDLES['product-rail'];
      if (this.dataset.ezFreeScroll != null) this.cfg = Object.assign({}, this.cfg, { free: true });

      this.track = this.querySelector('[data-ez-carousel-track]');
      if (!this.track) return;

      var cellSel = this.dataset.ezCell || '.ez-card';
      this.cells = Array.prototype.slice.call(this.track.querySelectorAll(cellSel));

      this.prevBtn = this.querySelector('[data-ez-carousel-prev]');
      this.nextBtn = this.querySelector('[data-ez-carousel-next]');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-ez-carousel-dot]'));
      this.progress = this.cfg.progress ? this.querySelector('[data-ez-carousel-progress]') : null;
      this._ticking = false;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._step.bind(this, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._step.bind(this, 1));
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', this._toCell.bind(this, i));
      }, this);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);

      this._afterBoot();      // subclass extras hook (rail swatches/hover, shop sheet)
      this.syncControls();
    }

    // Hooks for subclasses; no-ops in the base engine.
    _afterBoot() {}
    _afterResize() {}

    disconnectedCallback() {
      if (this.track && this._onScroll) this.track.removeEventListener('scroll', this._onScroll);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    _onResize() {
      this.syncControls();
      this._afterResize();
    }

    // Advance one step. Three modes cover all four finite families:
    //  - viewport   (hero pager, gallery scene, free-scroll): scroll by one viewport width.
    //  - mandatory  (banner): scroll to the next/prev slide START (mandatory re-snaps to a start
    //    either way, so a right-aligned prev target would only fight the snap — [B7]).
    //  - proximity  (product rail): direction-aware — NEXT left-aligns the incoming cell, PREV
    //    right-aligns the returning cell (proximity snap keeps the asymmetric targets).
    _step(dir) {
      if (!this.track) return;
      var beh = reduceMotion.matches ? 'auto' : 'smooth';

      if (this.cfg.step === 'viewport' || !this.cells.length) {
        this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: beh });
        return;
      }

      var sl = this.track.scrollLeft;
      var cw = this.track.clientWidth;
      var base = this.track.offsetLeft;
      var target = null, i;
      if (dir > 0) {
        for (i = 0; i < this.cells.length; i++) {
          if (this.cells[i].offsetLeft - base > sl + 2) { target = this.cells[i]; break; }
        }
        if (target) this.track.scrollTo({ left: target.offsetLeft - base, behavior: beh });
      } else {
        for (i = 0; i < this.cells.length; i++) {
          if (this.cells[i].offsetLeft - base < sl - 2) target = this.cells[i];
        }
        if (!target) return;
        var left = target.offsetLeft - base;
        // proximity rail: right-align the returning cell; mandatory banner: left-align.
        if (this.cfg.step === 'proximity') left = Math.max(0, left + target.offsetWidth - cw);
        this.track.scrollTo({ left: left, behavior: beh });
      }
    }

    _toCell(i) {
      var cell = this.cells[i];
      if (!cell) return;
      // Honour scroll-padding-inline-start so the target lands ON the CSS snap point. The mobile product
      // rail insets its snap by the edge-peek (direction-aware bidirectional peek); without this a dot
      // tap would smooth-scroll to the flush position and proximity-snap would then tug it inward by the
      // peek (a visible settle). Every other family (and the desktop rail) has 0 inset -> unchanged.
      this.track.scrollTo({
        left: Math.max(0, cell.offsetLeft - this.track.offsetLeft - this._snapInset()),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    // Parsed scroll-padding-inline-start of the track (0 when auto/none/unsupported). Aligns programmatic
    // scroll targets with the CSS snap points; returns 0 for every carousel family except the mobile
    // product-rail edge-peek, so it is a no-op everywhere else.
    _snapInset() {
      if (!this.track) return 0;
      var cs = window.getComputedStyle(this.track);
      var v = cs.scrollPaddingInlineStart || cs.scrollPaddingLeft || '0';
      var n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(function () {
        this.syncControls();
        this._ticking = false;
      }.bind(this));
    }

    syncControls() {
      if (!this.track) return;
      var max = this.track.scrollWidth - this.track.clientWidth;
      var x = this.track.scrollLeft;

      // Free-scroll (collection rail) leaves the arrows always live; every other bundle disables
      // an arrow at the matching end.
      if (!this.cfg.free) {
        if (this.prevBtn) this.prevBtn.disabled = x <= 1;
        if (this.nextBtn) this.nextBtn.disabled = x >= max - 1;
      }

      if (this.progress) {
        var pct = max > 0 ? x / max : 0;
        this.progress.style.transform = 'scaleX(' + pct.toFixed(4) + ')';
      }

      if (this.dots.length) {
        var active = this._activeIndex(x, max);
        this.dots.forEach(function (dot, i) {
          var on = i === active;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }
    }

    _activeIndex(x) {
      if (this.cfg.index === 'clientWidth') {
        return Math.round(x / this.track.clientWidth);
      }
      if (this.cfg.index === 'perSlide') {
        var per = this.cells.length ? (this.track.scrollWidth / this.cells.length) : 1;
        var a = Math.round(x / per);
        return a > this.dots.length - 1 ? this.dots.length - 1 : a;
      }
      // nearest: the cell whose left edge is closest to the current scroll origin.
      var origin = this.track.scrollLeft + this.track.offsetLeft;
      var best = 0, bestDist = Infinity;
      for (var i = 0; i < this.cells.length; i++) {
        var d = Math.abs(this.cells[i].offsetLeft - origin);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }
  }

  // Expose the base so the rail + shoppable slim modules can extend it for their extras
  // (swatches / recommendations / hover-intent; the shared product bottom-sheet).
  window.EzCarousel = EzCarousel;
  window.ezCarouselReduceMotion = reduceMotion;

  customElements.define('ez-carousel', EzCarousel);
  // Hero pager + image banner carry NO extras, so the engine drives them directly under their OWN
  // tags (their markup / CSS / tag-display are unchanged; the old per-section modules are deleted).
  // Each configures itself from its data-ez-bundle + data-ez-cell like any <ez-carousel>. The rail +
  // shoppable modules define their OWN tags — they extend EzCarousel for their extras (swatches /
  // recommendations / the product bottom-sheet).
  if (!customElements.get('ez-hero')) customElements.define('ez-hero', class extends EzCarousel {});
  if (!customElements.get('ez-image-banner')) customElements.define('ez-image-banner', class extends EzCarousel {});
})();
